<template>
  <div class="fu-page">
    <div class="fu-container">
      <div class="fu-header">
        <div class="header-content">
          <h1 class="page-title">Usage des features workspace</h1>
          <p class="page-subtitle">
            Baguette « improve », réorganisation, périmètre, téléchargement, ajout de fichiers, upload de dossier
            <span v-if="totalUsages"> · {{ totalUsages }} usages · {{ totalUsers }} utilisateurs</span>
          </p>
        </div>
        <router-link :to="{ name: 'adminDashboard' }" class="back-button">← Retour au dashboard</router-link>
      </div>

      <div class="controls">
        <div class="presets">
          <button v-for="p in presets" :key="p.key"
                  :class="['preset-btn', { active: activePreset === p.key }]"
                  @click="applyPreset(p.key)">{{ p.label }}</button>
        </div>
        <div class="control-right">
          <label class="date-field">Du <input type="date" v-model="from" @change="onCustomDate" /></label>
          <label class="date-field">Au <input type="date" v-model="to" @change="onCustomDate" /></label>
          <button class="refresh-btn" @click="fetchData">↻ Rafraîchir</button>
        </div>
      </div>

      <div v-if="error" class="error-banner">{{ error }}</div>
      <div v-if="loading" class="loading">Chargement…</div>

      <template v-if="!loading && events.length">
        <!-- Une carte par feature -->
        <div class="kpi-row">
          <div v-for="e in events" :key="e.event" class="kpi-card">
            <div class="kpi-label">{{ e.label }}</div>
            <div class="kpi-value" :class="{ accent: e.total > 0 }">{{ e.total }}</div>
            <div class="kpi-sub">{{ e.users }} utilisateur{{ e.users > 1 ? 's' : '' }}<span v-if="e.cabinets"> · {{ e.cabinets }} cabinet{{ e.cabinets > 1 ? 's' : '' }}</span></div>
          </div>
          <div v-for="ev in missingEvents" :key="ev.event" class="kpi-card kpi-card--empty">
            <div class="kpi-label">{{ ev.label }}</div>
            <div class="kpi-value muted">0</div>
            <div class="kpi-sub">jamais utilisée</div>
          </div>
        </div>

        <!-- Focus baguette : taux de réponse -->
        <div class="panel">
          <h2 class="panel-title">Baguette « improve » — taux de réponse aux questions</h2>
          <div class="improve-row">
            <div class="mini-stat"><span class="mini-val">{{ improve.usages }}</span><span class="mini-lbl">reformulations</span></div>
            <div class="mini-stat"><span class="mini-val">{{ improve.avg_questions }}</span><span class="mini-lbl">questions posées (moy.)</span></div>
            <div class="mini-stat"><span class="mini-val accent">{{ improve.avg_answered }}</span><span class="mini-lbl">réponses (moy.)</span></div>
            <div class="mini-stat"><span class="mini-val">{{ improve.usages ? Math.round(100 * improve.zero_answer / improve.usages) : 0 }} %</span><span class="mini-lbl">sans aucune réponse</span></div>
          </div>
        </div>

        <!-- Top utilisateurs -->
        <div class="panel">
          <h2 class="panel-title">Utilisateurs ({{ topUsers.length }})</h2>
          <table class="fu-table">
            <thead>
              <tr><th>Utilisateur</th><th>Total</th><th>Détail</th><th>Dernier usage</th></tr>
            </thead>
            <tbody>
              <tr v-for="u in topUsers" :key="u.email">
                <td class="email">{{ u.email }}</td>
                <td class="num">{{ u.total }}</td>
                <td class="detail">
                  <span v-for="(n, lbl) in u.breakdown" :key="lbl" class="chip">{{ lbl }} ×{{ n }}</span>
                </td>
                <td class="when">{{ fmtDateTime(u.last_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="footnote">
          Un « usage » = 1 déclenchement (la baguette n'est comptée qu'une fois par
          reformulation). « Upload de dossier » est compté par fichier. Source :
          analytics.feature_events (prod). Segmentation cabinet quand le compte y est rattaché.
        </p>
      </template>
      <div v-else-if="!loading && !error" class="loading">Aucun usage sur la période.</div>
    </div>
  </div>
</template>

<script>
import { supabase } from "@/supabase/supabaseClient";

const API_BASE = "https://api.query-juriste.com";
const ALL_FEATURES = [
  { event: "improve_question", label: "Baguette « improve »" },
  { event: "workspace_reorganize", label: "Réorganisation IA" },
  { event: "workspace_scope_search", label: "Recherche à périmètre" },
  { event: "workspace_archive_download", label: "Téléchargement zip" },
  { event: "workspace_incremental_ingest", label: "Ajout de fichiers" },
  { event: "workspace_folder_upload", label: "Upload de dossier" },
];

export default {
  name: "FeatureUsagePage",
  data() {
    return {
      from: "", to: "",
      events: [], topUsers: [], improve: {}, timeline: [],
      totalUsages: 0, totalUsers: 0,
      loading: false, error: null, adminPassword: null,
      activePreset: "30d",
      presets: [
        { key: "7d", label: "7 j" },
        { key: "30d", label: "30 j" },
        { key: "90d", label: "90 j" },
        { key: "all", label: "Tout" },
      ],
    };
  },
  computed: {
    missingEvents() {
      const seen = new Set(this.events.map((e) => e.event));
      return ALL_FEATURES.filter((f) => !seen.has(f.event));
    },
  },
  mounted() {
    this.applyPreset("30d");
  },
  methods: {
    isoDay(d) { return d.toISOString().slice(0, 10); },
    fmtDateTime(s) {
      if (!s) return "—";
      const d = new Date(s);
      return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    },
    promptForPassword() {
      if (!this.adminPassword) {
        const pwd = prompt("Mot de passe administrateur requis:");
        if (!pwd) throw new Error("Mot de passe requis");
        this.adminPassword = pwd;
      }
      return this.adminPassword;
    },
    applyPreset(key) {
      this.activePreset = key;
      const today = new Date();
      const to = new Date(today);
      let from = new Date(today);
      if (key === "7d") from.setDate(to.getDate() - 6);
      else if (key === "30d") from.setDate(to.getDate() - 29);
      else if (key === "90d") from.setDate(to.getDate() - 89);
      else if (key === "all") from = new Date(2026, 6, 1); // juillet 2026 (sortie feature)
      this.from = this.isoDay(from);
      this.to = this.isoDay(to);
      this.fetchData();
    },
    onCustomDate() {
      if (this.from && this.to && this.from <= this.to) {
        this.activePreset = null;
        this.fetchData();
      }
    },
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await supabase.auth.getSession();
        const jwt = data?.session?.access_token;
        if (!jwt) throw new Error("Session expirée");
        const pwd = this.promptForPassword();
        const url = `${API_BASE}/copilot/admin/metrics/feature-usage?from=${this.from}&to=${this.to}`;
        const resp = await fetch(url, { headers: { Authorization: `Bearer ${jwt}`, "X-Admin-Password": pwd } });
        if (!resp.ok) {
          if (resp.status === 403) this.adminPassword = null;
          const e = await resp.json().catch(() => ({}));
          throw new Error(e.error || `Erreur ${resp.status}`);
        }
        const json = await resp.json();
        this.events = json.events || [];
        this.topUsers = json.top_users || [];
        this.improve = json.improve || {};
        this.timeline = json.timeline || [];
        this.totalUsages = json.total_usages || 0;
        this.totalUsers = json.total_users || 0;
      } catch (err) {
        this.error = err.message;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.fu-page { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); min-height: 100vh; padding: 2rem 1rem; }
.fu-container { max-width: 1200px; margin: 0 auto; }
.fu-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1.5rem; color: #fff; }
.header-content { flex: 1; }
.page-title { font-size: 2rem; font-weight: 700; color: #fff; margin: 0 0 0.4rem; text-shadow: 0 2px 4px rgba(0,0,0,0.3); }
.page-subtitle { color: #fff; opacity: 0.85; font-size: 0.95rem; margin: 0; }
.back-button { background: rgba(255,255,255,0.15); color: #fff; border: 1px solid rgba(255,255,255,0.3); border-radius: 8px; padding: 8px 16px; text-decoration: none; font-size: 0.9rem; white-space: nowrap; transition: all 0.3s ease; }
.back-button:hover { background: rgba(255,255,255,0.25); }
.controls { display: flex; flex-wrap: wrap; gap: 16px; justify-content: space-between; align-items: center; background: rgba(255,255,255,0.95); padding: 1.25rem 1.5rem; border-radius: 16px; box-shadow: 0 8px 32px rgba(0,0,0,0.1); margin-bottom: 1.5rem; }
.presets { display: flex; gap: 6px; flex-wrap: wrap; }
.preset-btn { padding: 6px 12px; border: 1px solid #e2e8f0; background: #fff; border-radius: 8px; cursor: pointer; font-size: 13px; color: #4a5568; transition: all 0.2s ease; }
.preset-btn:hover { border-color: #667eea; color: #667eea; }
.preset-btn.active { background: linear-gradient(135deg, #667eea, #764ba2); color: #fff; border-color: transparent; }
.control-right { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; }
.date-field { font-size: 13px; color: #4a5568; }
.date-field input { margin-left: 4px; padding: 4px 6px; border: 1px solid #e2e8f0; border-radius: 6px; }
.refresh-btn { padding: 6px 12px; border: 1px solid #e2e8f0; background: #fff; border-radius: 8px; cursor: pointer; font-size: 13px; color: #667eea; }
.refresh-btn:hover { border-color: #667eea; }
.kpi-row { display: grid; grid-template-columns: repeat(6, 1fr); gap: 12px; margin-bottom: 1.5rem; }
.kpi-card { background: rgba(255,255,255,0.95); border-radius: 16px; padding: 1.25rem; box-shadow: 0 8px 32px rgba(0,0,0,0.1); transition: transform 0.2s ease, box-shadow 0.2s ease; }
.kpi-card:hover { transform: translateY(-3px); box-shadow: 0 12px 40px rgba(0,0,0,0.15); }
.kpi-card--empty { opacity: 0.6; }
.kpi-label { font-size: 12px; color: #718096; font-weight: 500; }
.kpi-value { font-size: 24px; font-weight: 700; color: #2d3748; margin: 4px 0 2px; }
.kpi-value.accent { color: #667eea; }
.kpi-value.muted { color: #cbd5e0; }
.kpi-sub { font-size: 11px; color: #a0aec0; }
.panel { background: rgba(255,255,255,0.95); border-radius: 16px; padding: 1.25rem 1.5rem; box-shadow: 0 8px 32px rgba(0,0,0,0.1); margin-bottom: 1.5rem; }
.panel-title { font-size: 15px; font-weight: 700; color: #2d3748; margin: 0 0 1rem; }
.improve-row { display: flex; gap: 2rem; flex-wrap: wrap; }
.mini-stat { display: flex; flex-direction: column; }
.mini-val { font-size: 22px; font-weight: 700; color: #2d3748; }
.mini-val.accent { color: #667eea; }
.mini-lbl { font-size: 11px; color: #a0aec0; }
.fu-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.fu-table th { text-align: left; color: #718096; font-weight: 600; padding: 6px 10px; border-bottom: 2px solid #edf2f7; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
.fu-table td { padding: 8px 10px; border-bottom: 1px solid #f1f5f9; color: #2d3748; vertical-align: top; }
.fu-table .email { font-weight: 500; }
.fu-table .num { font-weight: 700; color: #667eea; }
.fu-table .when { color: #a0aec0; white-space: nowrap; }
.chip { display: inline-block; background: #f5f3ff; color: #6c63ff; border-radius: 10px; padding: 2px 8px; font-size: 11px; margin: 0 4px 4px 0; white-space: nowrap; }
.footnote { font-size: 12px; color: rgba(255,255,255,0.75); margin-top: 4px; font-style: italic; }
.error-banner { background: rgba(254,242,242,0.9); color: #b91c1c; border-radius: 12px; padding: 1rem; margin-bottom: 1rem; }
.loading { color: #fff; padding: 40px; text-align: center; }
@media (max-width: 900px) { .kpi-row { grid-template-columns: repeat(2, 1fr); } .fu-header { flex-direction: column; gap: 1rem; align-items: flex-start; } }
</style>
